import { FC, useCallback, useMemo } from "react";
import type { EditorView } from "@tiptap/pm/view";
import RichTextEditor from "@src/features/libraryItem/course/RichTextEditor";
import { emptyTranslationSet } from "@src/features/knowledgeCheck/utils/prompts";
import {
  updateTranslationSet,
  useTranslation,
} from "@src/features/contentDetail/utils/translationSets";
import { TranslationModeContext } from "@src/features/libraryItem/course/contexts/TranslationModeContextProvider";
import { useContext } from "react";
import { useAtomValue } from "jotai";
import {
  trainingResourceTitleAtom,
  trainingResourceVersionAtom,
} from "../atoms";
import { useDispatchSetRichTextDocumentContentAction } from "../hooks/useDispatchSetRichTextDocumentContentAction";
import { useDispatchSetTrainingResourceNameAction } from "../hooks/useDispatchSetTrainingResourceNameAction";
import { useTrainingResourceContentPermissions } from "../TrainingResourcePageHeader";
import useTrainingResourceViewModeQueryParams from "../hooks/useTrainingResourceViewModeQueryParams";
import { cn } from "@src/lib/utils/cn";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import useSyncActiveClient from "@src/features/libraryItem/hooks/useSyncActiveClient";
import useVersionQueryParams from "@src/features/libraryItem/hooks/useVersionQueryParams";
import { TrainingResourceImageLinkBubbleMenu } from "@src/features/libraryItem/course/TrainingResourceImageLinkBubbleMenu";
import { TrainingResourceImageBubbleMenu } from "./TrainingResourceImageBubbleMenu";
import TrainingResourceRichTextEditorBottomToolbar from "./TrainingResourceRichTextEditorBottomToolbar";

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Title + body combined into one HTML document, the title as the first
// block. Shared with the converting-doc stream view so the in-progress
// document renders with exactly the structure (and therefore the
// first-block title styling) it will have in this editor when done.
export const buildCombinedDocContent = (
  titleText: string,
  bodyContent: string,
): string =>
  `<h1 data-opus-title="true">${escapeHtml(titleText)}</h1>${bodyContent || ""}`;

// Fixes a tiptap editor bug where imgs are not
// rendered when imgs are wrapped in p tags.
const removeImageParentParagraphs = (content: string): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = content;

  const paragraphs = tempDiv.querySelectorAll("p");
  paragraphs.forEach((p) => {
    const images = p.querySelectorAll("img");
    if (images.length > 0) {
      images.forEach((img) => {
        img.addEventListener("mouseover", () => {
          img.click();
        });
        p.parentNode?.insertBefore(img, p);
      });
      if (!p.textContent?.trim()) {
        p.remove();
      }
    }
  });

  return tempDiv.innerHTML;
};

// Function to extract body content from full HTML document
export const extractBodyContent = (htmlContent: string): string => {
  const processedContent = removeImageParentParagraphs(htmlContent);

  if (!processedContent.includes("<!DOCTYPE html>")) {
    return processedContent;
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = processedContent;

  const firstH2 = tempDiv.querySelector("h2") as HTMLHeadingElement | null;
  if (!firstH2) {
    return processedContent;
  }

  let content = "";
  let currentElement: Element | null = firstH2;
  while (currentElement) {
    content += currentElement.outerHTML;
    currentElement = currentElement.nextElementSibling;
  }

  return content;
};

type Props = {
  /**
   * Force read-only on top of content permissions (agent applies are
   * full-document replaces, so mid-conversion edits would be clobbered).
   */
  readOnly?: boolean;
};

const TrainingResourceRichTextEditor: FC<Props> = (props) => {
  const translationModeContext = useContext(TranslationModeContext);
  const dispatchTrainingResourceAction =
    useDispatchSetRichTextDocumentContentAction();
  const dispatchSetTrainingResourceNameAction =
    useDispatchSetTrainingResourceNameAction();
  const trainingResourceVersion = useAtomValue(trainingResourceVersionAtom);
  const trainingResourceTitle = useAtomValue(trainingResourceTitleAtom);
  const currentContent =
    trainingResourceVersion?.richTextDocument?.content || emptyTranslationSet();
  const currentContentTranslation = useTranslation(
    currentContent,
    translationModeContext?.currentLang || "en",
  );
  const titleTranslation = useTranslation(
    trainingResourceTitle,
    translationModeContext?.currentLang || "en",
  );
  const titleText = titleTranslation.text ?? "";
  const richTextDocumentId = trainingResourceVersion?.richTextDocument?.id;
  const contentPermissions = useTrainingResourceContentPermissions();
  const { trainingResourceViewMode } = useTrainingResourceViewModeQueryParams();
  useSyncActiveClient();
  const { version } = useVersionQueryParams();

  const bodyContent = useMemo(
    () => extractBodyContent(currentContentTranslation.text ?? ""),
    [currentContentTranslation.text],
  );

  // Combine title + body into a single HTML document so the title lives
  // inside the tiptap document as its first block. This makes selection,
  // copy, and keyboard navigation span from title through body naturally.
  const combinedContent = useMemo(
    () => buildCombinedDocContent(titleText, bodyContent),
    [titleText, bodyContent],
  );

  // Protect the title (first block) from being merged with the body.
  // Specifically: Delete at end of the title and Backspace at start of
  // the second block would both merge the two into one node; intercept
  // those key presses so the title remains its own, independent block.
  const protectTitleBlockKeyDown = useCallback(
    (view: EditorView, event: KeyboardEvent) => {
      const { selection } = view.state;
      if (selection.empty) {
        const $from = selection.$from;
        const atBlockStart = $from.parentOffset === 0;
        const atBlockEnd = $from.parentOffset === $from.parent.content.size;
        const topLevelIndex = $from.index(0);

        // Backspace at start of second top-level block → would merge body
        // content up into the title. Block it.
        if (event.key === "Backspace" && atBlockStart && topLevelIndex === 1) {
          event.preventDefault();
          return true;
        }

        // Delete at end of first top-level block → would pull the next
        // block's content into the title. Block it.
        if (event.key === "Delete" && atBlockEnd && topLevelIndex === 0) {
          event.preventDefault();
          return true;
        }
      }

      return false;
    },
    [],
  );

  const handleContentChange = useCallback(
    (html: string) => {
      // Split the editor HTML into title (first block) + body (rest).
      // Whatever block appears first is always treated as the title —
      // this guarantees a title always exists and cannot be deleted.
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const firstElement = tempDiv.firstElementChild;

      let newTitleText = titleText;
      if (firstElement) {
        newTitleText = firstElement.textContent || "";
        firstElement.remove();
      }

      const newBodyHtml = tempDiv.innerHTML;

      if (
        newTitleText !== titleText &&
        trainingResourceTitle &&
        translationModeContext
      ) {
        const updatedTranslationSet = updateTranslationSet({
          ts: trainingResourceTitle,
          lang: translationModeContext.currentLang,
          text: newTitleText,
        });
        dispatchSetTrainingResourceNameAction({
          newName: updatedTranslationSet,
          oldName: trainingResourceTitle,
        });
      }

      if (richTextDocumentId) {
        dispatchTrainingResourceAction({
          richTextDocumentId: richTextDocumentId,
          newText: newBodyHtml,
        });
      }
    },
    [
      richTextDocumentId,
      dispatchTrainingResourceAction,
      titleText,
      trainingResourceTitle,
      translationModeContext,
      dispatchSetTrainingResourceNameAction,
    ],
  );

  return (
    <div className="relative flex flex-1 flex-col self-stretch">
      <ScrollableFlexGrow
        flex={1}
        alignSelf="stretch"
        innerStyle={{
          display: "flex",
          alignItems: "center",
          paddingBlock: 48,
          paddingInline: 16,
        }}
      >
        <div
          className={cn(
            "flex flex-1 flex-col transition-all duration-300 ease-in-out",
            version === "published" && "pointer-events-none",
            trainingResourceViewMode === "mobile-view"
              ? "min-h-[800px] w-[420px] rounded-[30px] border bg-card p-6"
              : "min-h-[1200px] w-full max-w-[800px] pb-20",
          )}
        >
          <RichTextEditor
            text={combinedContent}
            editorParentClassName="pb-24"
            hasEditContentPermission={
              (contentPermissions?.hasEditContentPermission ?? false) &&
              !props.readOnly
            }
            onChange={handleContentChange}
            handleKeyDown={protectTitleBlockKeyDown}
            // The first block of the document is always treated as the
            // document title, so we show a title-specific placeholder on
            // it and no placeholder on other empty blocks.
            placeholder={({ pos }) => (pos === 0 ? "Give it a title..." : "")}
            showPlaceholderOnEveryEmptyNode
            // eslint-disable-next-line tailwindcss/no-custom-classname
            className="rich-text-opus-doc-editor"
            disableTextVariants
            enableMathematics={true}
            enableGapcursor={true}
            customLinkEditor={TrainingResourceImageLinkBubbleMenu}
            customImageBubbleMenu={TrainingResourceImageBubbleMenu}
          />
        </div>
      </ScrollableFlexGrow>
      <TrainingResourceRichTextEditorBottomToolbar
        showEditControls={
          version !== "published" &&
          !!contentPermissions?.hasEditContentPermission &&
          !props.readOnly
        }
      />
    </div>
  );
};

export default TrainingResourceRichTextEditor;
